/**
 * The web need score, client side: the bands the UI colours by and the four
 * endpoints behind them.
 *
 * Server counterpart: `apps/api/src/modules/ariregister/lib/webneed/`. The score
 * itself is computed there and stored on the company row - this file never
 * calculates one, it only reads and explains.
 *
 * ⚠ `WEB_NEED_HIGH` / `WEB_NEED_MID` are repeated from `webneed/score.ts`
 * because the CRM cannot import from the API package. They are the only numbers
 * duplicated across that boundary, they are exported as constants on both sides
 * rather than written as literals, and changing one without the other makes the
 * band split in the recompute panel disagree with the colours in the table
 * beside it. Change both or neither.
 */
import { api } from "./api";

export const WEB_NEED_HIGH = 70;
export const WEB_NEED_MID = 45;

export type WebNeedBand = "high" | "mid" | "low" | "none";

/** `none` covers both "scored zero" and "never computed": neither is a company
 *  worth calling today, and the cell says which one it is in words. */
export function webNeedBand(score: number | null | undefined): WebNeedBand {
  if (score == null || score <= 0) return "none";
  if (score >= WEB_NEED_HIGH) return "high";
  if (score >= WEB_NEED_MID) return "mid";
  return "low";
}

/** One line of the "why this number" breakdown. `id` is an i18n key under
 *  `ariregister.webNeed.reason`. */
export interface WebNeedReason {
  id: string;
  side: "gap" | "reach";
  points: number;
}

export interface WebNeedExplanation {
  score: number;
  /** How weak the web presence looks, 0-100. */
  gap: number;
  /** How much the company is worth calling, 0-100. */
  reach: number;
  reasons: WebNeedReason[];
  /** What the list and map are currently sorting on. Differs from `score` when
   *  the stored column is out of date. */
  storedScore: number | null;
  computedAt: string | null;
  /** The domain the scan and DNS evidence was looked up under. */
  domain: string | null;
  hasScan: boolean;
  hasDomainCheck: boolean;
}

export const getWebNeed = (regCode: string, signal?: AbortSignal): Promise<WebNeedExplanation> =>
  api.get<WebNeedExplanation>(`/admin/ariregister/${encodeURIComponent(regCode)}/webneed`, signal);

/** How much of the register carries a score, and how it splits. */
export interface WebNeedProgress {
  total: number;
  scored: number;
  bands: { high: number; mid: number; low: number; none: number };
  computedAt: string | null;
  running: boolean;
  doneThisRun: number;
  lastError: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export const getWebNeedProgress = (): Promise<WebNeedProgress> =>
  api.get<WebNeedProgress>("/admin/ariregister/webneed");

export const startWebNeedPass = (): Promise<{ started: boolean; alreadyRunning: boolean }> =>
  api.post("/admin/ariregister/webneed/start", {});

export const stopWebNeedPass = (): Promise<{ ok: boolean }> =>
  api.post("/admin/ariregister/webneed/stop", {});
